import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { MapPin, Phone, Mail, User, Send } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const handleSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    console.log("=== DÉBUT ENVOI FORMULAIRE ===");
    console.log("Données du formulaire:", data);
    
    try {
      console.log("Appel de la fonction Supabase...");
      
      const response = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name,
          email: data.email,
          subject: data.subject,
          message: data.message,
        }
      });

      console.log("Réponse complète de Supabase:", response);

      if (response.error) {
        console.error("Erreur de la fonction Supabase:", response.error);
        throw new Error(response.error.message || "Erreur lors de l'envoi");
      }

      if (!response.data?.success) {
        console.error("La fonction a retourné un échec:", response.data);
        throw new Error(response.data?.error || "L'envoi a échoué");
      }

      console.log("Email envoyé avec succès! ID:", response.data?.emailId);
      
      toast.success("Message envoyé avec succès! Nous vous répondrons dans les plus brefs délais.");
      form.reset();
      
    } catch (error: any) {
      console.error("=== ERREUR LORS DE L'ENVOI ===");
      console.error("Type d'erreur:", error.constructor.name);
      console.error("Message:", error.message);
      console.error("Détails:", error);
      console.error("=== FIN ERREUR ===");
      
      // Message d'erreur plus descriptif pour l'utilisateur
      let userMessage = "Erreur lors de l'envoi du message. ";
      if (error.message?.includes("Configuration manquante")) {
        userMessage += "Service temporairement indisponible.";
      } else if (error.message?.includes("Resend")) {
        userMessage += "Problème avec le service d'envoi d'emails.";
      } else {
        userMessage += "Veuillez réessayer ou nous contacter directement.";
      }
      
      toast.error(userMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer 
      title="Contactez-nous"
      variant="default"
      intensity="low"
      className="py-6"
    >
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            <p className="text-muted-foreground">
              Nous sommes là pour vous aider. N'hésitez pas à nous contacter pour toute question
              ou suggestion concernant notre application.
            </p>
            
            <div className="space-y-4">
              <div className="glass-morphism p-4 rounded-lg flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Notre Adresse</h3>
                  <p className="text-muted-foreground text-sm">Cap-Haïtien, Haïti</p>
                </div>
              </div>
              
              <div className="glass-morphism p-4 rounded-lg flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Phone className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Téléphone</h3>
                  <p className="text-muted-foreground text-sm">+90 551 097 4587</p>
                </div>
              </div>
              
              <div className="glass-morphism p-4 rounded-lg flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Email</h3>
                  <p className="text-muted-foreground text-sm">alexiscarlenockson98@gmail.com</p>
                </div>
              </div>

              <div className="glass-morphism p-4 rounded-lg flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Horaires d'ouverture</h3>
                  <p className="text-muted-foreground text-sm">Lundi - Vendredi : 9h - 18h</p>
                </div>
              </div>
            </div>
            
            <div className="glass-morphism p-4 rounded-lg h-64 relative overflow-hidden">
              <div className="absolute inset-0 bg-blue-50/50"></div>
              <div className="relative z-10 h-full flex items-center justify-center">
                <p className="text-primary font-medium">Carte interactive</p>
              </div>
            </div>
          </div>
          
          <div className="glass-morphism p-6 rounded-lg animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <h2 className="text-xl font-bold mb-4 text-primary">Envoyez-nous un message</h2>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ required: "Le nom est requis" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom</FormLabel>
                      <FormControl>
                        <Input placeholder="Votre nom" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  rules={{ 
                    required: "L'email est requis",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Veuillez entrer un email valide"
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="votre@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subject"
                  rules={{ required: "Le sujet est requis" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sujet</FormLabel>
                      <FormControl>
                        <Input placeholder="Sujet de votre message" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="message"
                  rules={{ required: "Le message est requis" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Votre message..." rows={5} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button className="w-full" type="submit" disabled={isSubmitting}>
                  <Send className="mr-2 h-4 w-4" />
                  {isSubmitting ? "Envoi..." : "Envoyer"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Contact;
